import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { MapPin, Music, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Artist } from 'types';

interface ConnectConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedArtists: Artist[];
  onConfirm: () => void;
  error: string | null;
}

export function ConnectConfirmationModal({
  isOpen,
  onClose,
  selectedArtists,
  onConfirm,
  error,
}: ConnectConfirmationModalProps) {
  const { t } = useTranslation('collab-club');
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t('connect_with_artists')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="text-center">
            <p className="text-gray-600">{t('about_to_send', { count: selectedArtists.length })}</p>
          </div>

          {/* Selected Artists List */}
          <div className="space-y-4">
            {selectedArtists.map((artist, index) => (
              <div
                key={artist.id}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={artist.image || '/placeholder.svg'} alt={artist.name} />
                    <AvatarFallback className="text-sm">
                      {artist.name
                        .split(' ')
                        .map((n) => n[0])
                        .join('')}
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900 truncate">{artist.name}</h3>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Music className="h-3 w-3" />
                        {artist.genres?.join(', ') || 'Unknown'}
                      </span>

                      {artist.country && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {artist.country}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-1 mt-2">
                      {artist.skills &&
                        artist.skills.map((skill) => (
                          <Badge key={skill} variant="secondary" className="text-xs capitalize">
                            {skill}
                          </Badge>
                        ))}
                    </div>
                  </div>
                </div>

                <div className="text-sm text-gray-500">#{index + 1}</div>
              </div>
            ))}
          </div>

          {/* Connection Message Preview */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">{t('request_includes')}</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• {t('request_includes_1')}</li>
              <li>• {t('request_includes_2')}</li>
              <li>• {t('request_includes_3')}</li>
              <li>• {t('request_includes_4')}</li>
            </ul>
          </div>
        </div>

        {error && <div className="text-red-500 mt-2">{error}</div>}

        <DialogFooter className="flex gap-3 pt-6">
          <Button variant="outline" onClick={onClose} className="flex-1 bg-transparent">
            {t('cancel')}
          </Button>
          <Button onClick={onConfirm} className="flex-1 bg-blue-500 hover:bg-blue-600">
            {t('send_request', { count: selectedArtists.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
